import { DeleteOutlined, DownOutlined, HolderOutlined } from "@ant-design/icons"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Col, DatePicker, Flex, Form, type FormListFieldData, Row } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import dayjs, { type Dayjs } from "dayjs"
import React, { useState } from "react"

import { PlannedMoneyInput } from "../../../../../components/PlannedMoneyInput.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import type {
  ProjectFormData,
  ProjectFormDataPhase
} from "../../../project-form-data.ts"
import { MilestoneChecklist } from "./MilestoneChecklist.tsx"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"

export interface MilestoneField extends FormListFieldData {
  phase: ProjectFormDataPhase
}

type FieldType = ProjectFormData["project_phases"]

interface MilestonesItemProps {
  id: string
  fieldName: number
  form: FormInstance<ProjectFormData>
  onRemove: () => void
}

export function MilestonesItem({
  id,
  fieldName,
  form,
  onRemove
}: MilestonesItemProps) {
  const { getDateValueProps, createDateRangeRules } = useDateFormat()
  const [collapsed, setCollapsed] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: collapsed })

  const toggleCollapsed = () => {
    if (!collapsed) {
      return setCollapsed(true)
    }

    const values: ProjectFormDataPhase["checklist_data"] = form.getFieldValue([
      "project_phases",
      fieldName,
      "checklist_data"
    ])

    const invalidFields = values?.reduce<number[]>((indexes, e, i) => {
      if (!e?.name?.trim()) indexes.push(i)
      return indexes
    }, [])

    if (invalidFields?.length) {
      void form.validateFields(
        invalidFields?.map((index) => [
          "project_phases",
          fieldName,
          "checklist_data",
          index,
          "name"
        ])
      )
    } else {
      setCollapsed((prevState) => !prevState)
    }
  }

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
          opacity: 0.5
        }
      : {})
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-b border-border even:bg-primary/5 last:border-none py-2 px-1`}
    >
      <Row className={"flex-nowrap"} gutter={8}>
        <Col flex={"70px"}>
          <Flex gap={5}>
            <Button
              icon={<HolderOutlined />}
              style={{ cursor: "move" }}
              disabled={collapsed}
              {...attributes}
              {...listeners}
            />
            <Button
              icon={<DownOutlined />}
              onClick={toggleCollapsed}
              className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
            />
          </Flex>
        </Col>
        <Col flex={"auto"} className={"min-w-36"}>
          <Form.Item<FieldType>
            noStyle
            name={[fieldName, "name"]}
            rules={[{ required: true, whitespace: true }]}
          >
            <TextInput placeholder="Enter name" />
          </Form.Item>
        </Col>
        <Col flex={"135px"}>
          <Form.Item<FieldType>
            noStyle
            name={[fieldName, "start_date"]}
            getValueProps={getDateValueProps}
            rules={createDateRangeRules(
              ["project_phases", fieldName, "end_date"],
              "start"
            )}
          >
            <DatePicker
              onChange={(value: Dayjs) => {
                form.validateFields([["project_phases", fieldName, "end_date"]])

                dateAutoFill<ProjectFormData>({
                  form,
                  endDatePath: ["project_phases", fieldName, "end_date"]
                })(value)
              }}
            />
          </Form.Item>
        </Col>
        <Col flex={"135px"}>
          <Form.Item<FieldType>
            noStyle
            name={[fieldName, "end_date"]}
            getValueProps={getDateValueProps}
            rules={createDateRangeRules(
              ["project_phases", fieldName, "start_date"],
              "end"
            )}
          >
            <DatePicker
              onChange={() => {
                form.validateFields([
                  ["project_phases", fieldName, "start_date"]
                ])
              }}
            />
          </Form.Item>
        </Col>
        <Col flex={"160px"}>
          <PlannedMoneyInput
            noStyle
            name={[fieldName, "planned_receivable"]}
            getValueFromEvent={(value) => value?.toFixed(2) ?? 0}
            normalize={(v) => v || 0}
          />
        </Col>
        <Col flex={"160px"}>
          <PlannedMoneyInput
            noStyle
            name={[fieldName, "planned_payable"]}
            getValueFromEvent={(value) => value?.toFixed(2) ?? 0}
            normalize={(v) => v || 0}
          />
        </Col>
        <Col flex={"42px"}>
          <Button
            className={"m-0"}
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Col>
      </Row>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          collapsed ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <MilestoneChecklist fieldName={fieldName} />
        </div>
      </div>
    </div>
  )
}
